import { useCallback, useEffect, useRef, useState } from "react";
/**
 * Determine the best supported audio MIME type for MediaRecorder.
 * Prefers webm/opus (Chrome/Firefox), falls back to mp4 (Safari).
 */ function getPreferredMimeType() {
    const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4"
    ];
    for (const mimeType of candidates){
        if (MediaRecorder.isTypeSupported(mimeType)) {
            return mimeType;
        }
    }
    // Fallback — let the browser pick its default
    return "";
}
const STOP_TIMEOUT_MS = 5000;
/**
 * Records audio through the browser's MediaRecorder API.
 *
 * Package-private: it is the transport half of `useVoiceInput`, which is the
 * supported entry point. Kept apart from it so the recording mechanics — mime
 * negotiation, releasing the mic, the stop safety net — stay readable on their
 * own.
 */ export function useAudioRecorder() {
    const [state, setState] = useState("idle");
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    /** Set for the whole of `startRecording`, including the permission await. */ const isStartingRef = useRef(false);
    /**
   * Bumped by every start and by every stop, so a permission that resolves late
   * can tell whether it is still the request the hook is waiting on.
   */ const requestRef = useRef(0);
    const isMountedRef = useRef(true);
    const releaseStream = useCallback(()=>{
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track)=>track.stop());
            streamRef.current = null;
        }
    }, []);
    useEffect(function releaseMicOnUnmount() {
        isMountedRef.current = true;
        return ()=>{
            isMountedRef.current = false;
            if (mediaRecorderRef.current?.state === "recording") {
                mediaRecorderRef.current.stop();
            }
            mediaRecorderRef.current = null;
            chunksRef.current = [];
            // Inline release to avoid stale ref in cleanup
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track)=>track.stop());
                streamRef.current = null;
            }
        };
    }, []);
    const startRecording = useCallback(async ()=>{
        // Synchronous, because `getUserMedia` resolves on a later task: a second
        // click before then would acquire a SECOND stream and overwrite the only
        // ref tracking the first, leaving a live microphone nothing can stop.
        if (isStartingRef.current || mediaRecorderRef.current) {
            return {
                status: "aborted"
            };
        }
        isStartingRef.current = true;
        const request = ++requestRef.current;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true
            });
            // The permission prompt outlives the surface that asked for it. A grant
            // that lands after the unmount — or after a stop already moved on — must
            // release its tracks here, or the recording indicator stays on with no
            // recorder left to stop.
            if (!isMountedRef.current || request !== requestRef.current) {
                stream.getTracks().forEach((track)=>track.stop());
                return {
                    status: "aborted"
                };
            }
            streamRef.current = stream;
            const mimeType = getPreferredMimeType();
            const recorder = new MediaRecorder(stream, mimeType ? {
                mimeType
            } : undefined);
            chunksRef.current = [];
            recorder.ondataavailable = (event)=>{
                if (event.data.size > 0) {
                    chunksRef.current.push(event.data);
                }
            };
            mediaRecorderRef.current = recorder;
            recorder.start();
            setState("recording");
            return {
                status: "started"
            };
        } catch (error) {
            // Including a `recorder.start()` that threw — the ref is assigned just
            // before it. The start guard reads that ref, so a recorder left in it
            // refuses every later click for the rest of the session, and refuses it
            // in silence, because an aborted start is nothing to report.
            mediaRecorderRef.current = null;
            releaseStream();
            setState("idle");
            const denied = error instanceof DOMException && error.name === "NotAllowedError";
            return {
                status: "failed",
                error: denied ? "mic-denied" : "record-failed"
            };
        } finally{
            isStartingRef.current = false;
        }
    }, [
        releaseStream
    ]);
    const stopRecording = useCallback(()=>{
        return new Promise((resolve)=>{
            // Retires any start still waiting on the permission prompt: it must
            // release the stream it is about to be handed rather than begin a
            // recording the user has already stopped.
            requestRef.current += 1;
            const recorder = mediaRecorderRef.current;
            if (!recorder || recorder.state !== "recording") {
                // Where a recorder that went inactive on its own lands — a revoked
                // permission, a device unplugged. Dropping it is what lets the user
                // start again; keeping it would leave the start guard tripped forever.
                mediaRecorderRef.current = null;
                setState("idle");
                releaseStream();
                resolve({
                    status: "nothing-recorded"
                });
                return;
            }
            const settle = (outcome)=>{
                chunksRef.current = [];
                mediaRecorderRef.current = null;
                releaseStream();
                setState("idle");
                resolve(outcome);
            };
            // Safety net: if onstop never fires, give up rather than hang the UI
            const timeout = setTimeout(()=>settle({
                    status: "failed",
                    error: "record-failed"
                }), STOP_TIMEOUT_MS);
            recorder.onerror = ()=>{
                clearTimeout(timeout);
                settle({
                    status: "failed",
                    error: "record-failed"
                });
            };
            recorder.onstop = ()=>{
                clearTimeout(timeout);
                const mimeType = recorder.mimeType || "audio/webm";
                settle({
                    status: "recorded",
                    blob: new Blob(chunksRef.current, {
                        type: mimeType
                    })
                });
            };
            recorder.stop();
        });
    }, [
        releaseStream
    ]);
    return {
        state,
        startRecording,
        stopRecording
    };
}

//# sourceMappingURL=useAudioRecorder.js.map